import { useState } from 'react'
import type { TweBrand, TweCategory } from 'api/generated'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Switch } from 'components/ui/switch'
import { ALL, FilterState, initialFilters } from 'helpers/twe-filters'

const GENDERS = ['unisex', 'uomo', 'donna']

interface TweFiltersSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: FilterState
  categories: TweCategory[]
  brands: TweBrand[]
  onApply: (filters: FilterState) => void
}

/**
 * The seven product-side filters. They narrow the set *before* the ranking, so
 * they're a deliberate act: edited as a draft here, applied in one go.
 */
export const TweFiltersSheet = ({
  open,
  onOpenChange,
  filters,
  categories,
  brands,
  onApply,
}: TweFiltersSheetProps) => {
  const [draft, setDraft] = useState(filters)

  // Reseed the draft when the sheet opens. Adjusting state during render rather
  // than in an effect: https://react.dev/learn/you-might-not-need-an-effect
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setDraft(filters)
  }

  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const apply = (next: FilterState) => {
    onApply(next)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>Filtri prodotto</SheetTitle>
          <SheetDescription>
            Restringono l&apos;insieme prima della classifica: con un brand
            selezionato ottieni la top N di quel brand.
          </SheetDescription>
        </SheetHeader>

        <div className='flex flex-col gap-4 px-4'>
          <div className='flex flex-col gap-1.5'>
            <Label>Categoria</Label>
            <Select
              value={draft.categoryId}
              onValueChange={(value) => set('categoryId', value)}
            >
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutte</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={String(category.id)}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Brand</Label>
            <Select
              value={draft.brand}
              onValueChange={(value) => set('brand', value)}
            >
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutti</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.name} value={brand.name}>
                    {brand.name} ({brand.products_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Genere</Label>
            <Select
              value={draft.gender}
              onValueChange={(value) => set('gender', value)}
            >
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutti</SelectItem>
                {GENDERS.map((gender) => (
                  <SelectItem key={gender} value={gender}>
                    {gender}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Prezzo</Label>
            <div className='grid grid-cols-2 gap-2'>
              <Input
                type='number'
                placeholder='min €'
                value={draft.minPrice}
                onChange={(e) => set('minPrice', e.target.value)}
              />
              <Input
                type='number'
                placeholder='max €'
                value={draft.maxPrice}
                onChange={(e) => set('maxPrice', e.target.value)}
              />
            </div>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Cerca</Label>
            <Input
              type='text'
              placeholder='Titolo o brand...'
              value={draft.q}
              onChange={(e) => set('q', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  apply(draft)
                }
              }}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='on-sale'>In saldo</Label>
            <Switch
              id='on-sale'
              checked={draft.onSale}
              onCheckedChange={(checked) => set('onSale', checked)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label htmlFor='discountable'>Coupon premium</Label>
            <Switch
              id='discountable'
              checked={draft.discountable}
              onCheckedChange={(checked) => set('discountable', checked)}
            />
          </div>
        </div>

        <SheetFooter className='flex-row gap-2'>
          <Button className='flex-1' onClick={() => apply(draft)}>
            Applica
          </Button>
          <Button
            variant='outline'
            onClick={() =>
              apply({
                ...initialFilters,
                period: draft.period,
                dateFrom: draft.dateFrom,
                dateTo: draft.dateTo,
                limit: draft.limit,
              })
            }
          >
            Azzera
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
